/**
 * Aba "Pneus" do Raio-X do veículo: configuração, mapa dos eixos, o painel da
 * posição clicada e o que a integração diz estar nesta placa sem posição no
 * SIAG. Uma chamada (`/fleet/vehicles/:plate/tire-map`) traz tudo.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TireConfigurationListDto, TireMapDto, TireMapPositionDto } from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { IconTire, IconX } from "../../components/icons";
import { TireMap } from "./TireMap";
import { MoveTireModal, type MoveTirePreset } from "./MoveTireModal";
import { InspectTireModal } from "./InspectTireModal";
import {
  ConditionBadge, fmtDate, fmtKm, fmtMm, fmtPsi, lifeText, OriginBadge, TireStatusBadge, useTireCapabilities,
} from "./tire-ui";
import fotoCavalo6x2 from "../../assets/pneus/cavalo-6x2.webp";
import fotoCarreta3 from "../../assets/pneus/carreta-3-eixos.webp";
import fotoCarreta4 from "../../assets/pneus/carreta-4-eixos.webp";

export function VehicleTiresTab({ plate }: { plate: string }) {
  const { can } = useTireCapabilities();
  const [sel, setSel] = useState<TireMapPositionDto | null>(null);
  const [mover, setMover] = useState<MoveTirePreset | null>(null);
  const [inspecionar, setInspecionar] = useState<TireMapPositionDto | null>(null);

  const mapa = useQuery({
    queryKey: ["tires", "map", plate],
    queryFn: () => api.get<TireMapDto>(`/fleet/vehicles/${encodeURIComponent(plate)}/tire-map`),
    retry: false,
  });
  const m = mapa.data;

  if (mapa.isLoading) return <div className="card p-6 text-sm text-gray-500">Carregando mapa de pneus…</div>;
  if (mapa.isError || !m) {
    return (
      <div className="card">
        <EmptyState icon={<IconTire className="w-6 h-6" />} title="Mapa de pneus indisponível"
          description={mapa.error instanceof ApiError ? mapa.error.message : "Não foi possível carregar os pneus deste veículo."} />
      </div>
    );
  }

  const selAtual = sel ? m.axles.flatMap((a) => a.positions).find((p) => p.id === sel.id) ?? null : null;

  return (
    <div className="space-y-4">
      <div className="card p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
        <div>
          <p className="text-xs text-gray-500">Veículo</p>
          <p className="font-mono font-semibold text-gray-900">{m.vehicle.plate}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Configuração</p>
          <p className="text-sm font-medium text-gray-900">{m.configuration?.name ?? "Não definida"}</p>
        </div>
        {m.configuration && (
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="badge-gray">{m.summary.occupied}/{m.summary.positions} posições ocupadas</span>
            {m.summary.empty > 0 && <span className="badge-gray">{m.summary.empty} vazia(s)</span>}
            {m.summary.attention > 0 && <span className="badge-amber">{m.summary.attention} em atenção</span>}
            {m.summary.critical > 0 && <span className="badge-red">{m.summary.critical} crítico(s)</span>}
          </div>
        )}
        <div className="ml-auto text-right">
          <p className="text-xs text-gray-500">KM atual</p>
          <p className="text-sm font-medium text-gray-900">{m.odometer ? fmtKm(m.odometer.km) : m.vehicle.isTrailer ? "Carreta (sem hodômetro)" : "—"}</p>
          {m.odometer && <p className={`text-[0.6875rem] ${m.odometer.stale ? "text-warning-700" : "text-gray-400"}`}>{m.odometer.sourceLabel}{m.odometer.stale ? " · leitura antiga" : ""}</p>}
        </div>
        {m.configuration && (
          <div className="flex gap-2 w-full sm:w-auto">
            {can("tires.inspect") && (
              <Link className="btn-secondary" to={`/app/frota/pneus/inspecao?placa=${encodeURIComponent(m.vehicle.plate)}`}>Nova inspeção</Link>
            )}
            {can("tires.move") && <button className="btn-primary" onClick={() => setMover({})}>Movimentar pneu</button>}
          </div>
        )}
      </div>

      {!m.configuration ? (
        <ConfigurationSetup map={m} canManage={can("tires.manage")} />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_22rem] gap-4 items-start">
          <div className="card p-4">
            <TireMap axles={m.axles} kind={m.vehicle.isTrailer ? "trailer" : "tractor"} selectedId={selAtual?.id} onSelect={setSel} />
          </div>
          <div className="hidden xl:block">
            {selAtual ? (
              <PositionPanel p={selAtual} plate={m.vehicle.plate} can={can} onMove={setMover} onInspect={setInspecionar} onClose={() => setSel(null)} />
            ) : (
              <div className="card p-5 text-sm text-gray-500">Clique numa posição do mapa para ver o pneu e as ações.</div>
            )}
          </div>
        </div>
      )}

      {/* Telas menores: painel como gaveta */}
      {selAtual && createPortal(
        <div className="xl:hidden fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-gray-900/30" onClick={() => setSel(null)} />
          <div className="relative w-full max-w-sm h-full bg-white shadow-xl overflow-y-auto">
            <PositionPanel p={selAtual} plate={m.vehicle.plate} can={can} onMove={setMover} onInspect={setInspecionar} onClose={() => setSel(null)} flat />
          </div>
        </div>,
        document.body,
      )}

      {m.unplacedFromIntegration.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Pneus da integração nesta placa sem posição no SIAG ({m.unplacedFromIntegration.length})</p>
            <p className="text-xs text-gray-500">A Finatto informa estes pneus em {m.vehicle.plate}. Instale-os na posição correta para o SIAG controlar o histórico.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Pneu</th>
                  <th className="text-left px-4 py-2">Marca</th>
                  <th className="text-left px-4 py-2">Posição na Finatto</th>
                  <th className="text-right px-4 py-2">Sulco</th>
                  <th className="text-left px-4 py-2">Origem</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {m.unplacedFromIntegration.map((t) => (
                  <tr key={t.id}>
                    <td className="px-4 py-2.5 font-mono"><Link className="hover:underline" to={`/frota/pneus/${t.id}`}>{t.code}</Link></td>
                    <td className="px-4 py-2.5 text-gray-600">{t.brand ?? "—"}</td>
                    <td className="px-4 py-2.5 text-gray-600">{t.location?.positionLabel ?? "—"}</td>
                    <td className="px-4 py-2.5 text-right">{fmtMm(t.treadMm)}</td>
                    <td className="px-4 py-2.5"><OriginBadge origin={t.source} /></td>
                    <td className="px-4 py-2.5 text-right">
                      {can("tires.move") && (
                        <button className="btn-ghost text-xs" onClick={() => setMover({ tireId: t.id, action: "install", destinationPlate: m.vehicle.plate })}>Instalar</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {mover && <MoveTireModal preset={mover} onClose={() => setMover(null)} />}
      {inspecionar?.tire && (
        <InspectTireModal tireId={inspecionar.tire.id} tireCode={inspecionar.tire.code} positionLabel={inspecionar.label}
          plate={m.vehicle.plate} onClose={() => setInspecionar(null)} />
      )}
    </div>
  );
}

function PositionPanel({ p, plate, can, onMove, onInspect, onClose, flat }: {
  p: TireMapPositionDto;
  plate: string;
  can: (c: "tires.view" | "tires.inspect" | "tires.move" | "tires.retread" | "tires.manage") => boolean;
  onMove: (preset: MoveTirePreset) => void;
  onInspect: (p: TireMapPositionDto) => void;
  onClose: () => void;
  flat?: boolean;
}) {
  const t = p.tire;
  const linha = (rotulo: string, valor: React.ReactNode) => (
    <div className="flex justify-between gap-3 py-1.5 border-b border-gray-50 text-sm">
      <span className="text-gray-500">{rotulo}</span><span className="text-gray-900 text-right">{valor}</span>
    </div>
  );
  return (
    <div className={flat ? "" : "card"}>
      <div className="flex items-start justify-between px-5 py-4 border-b border-surface-border">
        <div>
          <p className="text-xs text-gray-500">{p.label}</p>
          <p className="text-lg font-semibold text-gray-900 font-mono">{t ? `Pneu ${t.code}` : "Posição vazia"}</p>
        </div>
        <button className="btn-ghost p-1" onClick={onClose} aria-label="Fechar"><IconX className="w-5 h-5" /></button>
      </div>
      {t ? (
        <div className="px-5 py-3">
          <div className="flex flex-wrap gap-1.5 mb-2">
            <TireStatusBadge status={t.status} />
            <span className="badge-gray">{lifeText(t.life)}</span>
            {t.condition && t.condition !== "normal" && <ConditionBadge condition={t.condition} />}
          </div>
          {linha("Marca", t.brand ?? "—")}
          {linha("Modelo", t.model ?? "—")}
          {linha("Medida", t.size ?? "—")}
          {linha("Vida", t.life)}
          {linha("Sulco", <>{fmtMm(t.treadMm)} {t.treadOrigin && <OriginBadge origin={t.treadOrigin} />}</>)}
          {linha("Pressão", fmtPsi(t.pressurePsi))}
          {linha("KM acumulado", fmtKm(t.kmAccumulated))}
          {linha("Instalado em", p.installation ? fmtDate(p.installation.installedAt) : "—")}
          {linha("KM na instalação", fmtKm(p.installation?.installedOdometerKm))}
          {p.installation?.source === "finatto_bootstrap" && (
            <p className="mt-2 text-[0.6875rem] text-gray-500">Montagem herdada da Finatto. Data e KM reais desconhecidos.</p>
          )}
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Link className="btn-secondary justify-center" to={`/frota/pneus/${t.id}`}>Ver pneu</Link>
            {can("tires.move") && <button className="btn-secondary justify-center" onClick={() => onMove({ tireId: t.id })}>Movimentar</button>}
            {can("tires.inspect") && <button className="btn-secondary justify-center" onClick={() => onInspect(p)}>Inspecionar</button>}
            {can("tires.move") && <button className="btn-secondary justify-center" onClick={() => onMove({ tireId: t.id, action: "remove" })}>Retirar</button>}
          </div>
        </div>
      ) : (
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-gray-500">Nenhum pneu montado nesta posição no SIAG.</p>
          {can("tires.move") && (
            <button className="btn-primary w-full justify-center"
              onClick={() => onMove({ action: "install", destinationPlate: plate, destinationPositionId: p.id })}>
              Instalar pneu aqui
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Veículo sem desenho: sugere (Finatto ou tipo) e deixa escolher. Nada é aplicado sozinho. */
const DESENHOS_CAVALO = [
  { code: "cavalo-6x2", nome: "Cavalo 6x2", detalhe: "direcional + tração + truck", img: fotoCavalo6x2 },
];
const DESENHOS_CARRETA = [
  { code: "carreta-3-eixos", nome: "Carreta 3 eixos", detalhe: "três eixos duplos", img: fotoCarreta3 },
  { code: "carreta-4-eixos", nome: "Carreta 4 eixos", detalhe: "quatro eixos duplos", img: fotoCarreta4 },
];

function ConfigurationSetup({ map, canManage }: { map: TireMapDto; canManage: boolean }) {
  const qc = useQueryClient();
  const [escolha, setEscolha] = useState(map.suggestedConfiguration?.templateCode ? `tpl:${map.suggestedConfiguration.templateCode}` : "");
  const [erro, setErro] = useState<string | null>(null);
  const cfgs = useQuery({
    queryKey: ["tires", "configurations"],
    queryFn: () => api.get<TireConfigurationListDto>("/fleet/tire-configurations"),
    enabled: canManage,
  });
  const salvar = useMutation({
    mutationFn: () => api.put(`/fleet/vehicles/${encodeURIComponent(map.vehicle.plate)}/tire-configuration`,
      escolha.startsWith("tpl:") ? { templateCode: escolha.slice(4) } : { configurationId: escolha }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tires"] }),
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível salvar a configuração."),
  });
  const s = map.suggestedConfiguration;
  const sugestaoJaListada = !!s?.templateCode && (cfgs.data?.templates ?? []).some((t) => t.code === s.templateCode);
  // Desenhos da frota do cliente (cavalos 6x2; carretas de 3 e 4 eixos), com a
  // mesma imagem que o mapa usa. Modelo já usado vira configuração da empresa e
  // sai da lista de modelos — então o cartão aponta para ela quando existir.
  const opcoesVisuais = (map.vehicle.isTrailer ? DESENHOS_CARRETA : DESENHOS_CAVALO).map((o) => {
    const daEmpresa = (cfgs.data?.items ?? []).find((c) => c.active && c.code === o.code);
    return { ...o, valor: daEmpresa ? daEmpresa.id : `tpl:${o.code}` };
  });

  return (
    <div className="card p-5 space-y-3">
      <p className="text-sm font-semibold text-gray-900">Defina a configuração de eixos de {map.vehicle.plate}</p>
      <p className="text-sm text-gray-500">
        O mapa de pneus precisa saber quantos eixos o veículo tem e quais são duplos.
        {s && <> Sugestão: <strong>{s.name}</strong>{s.source === "finatto" ? ` (pela configuração informada na Finatto: ${s.finattoTireConfig})` : " (pelo tipo do veículo, confira)"}.</>}
      </p>
      {canManage ? (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-3">
            {opcoesVisuais.map((o) => (
              <button
                key={o.code}
                type="button"
                onClick={() => setEscolha(o.valor)}
                aria-pressed={escolha === o.valor}
                className={`w-36 rounded-xl border-2 bg-white p-2 text-left transition hover:shadow-card-md
                  ${escolha === o.valor ? "border-tm-500 ring-2 ring-tm-200" : "border-surface-border"}`}
              >
                <img src={o.img} alt="" className="w-full h-44 object-contain" />
                <p className="mt-1 text-sm font-medium text-gray-900">{o.nome}</p>
                <p className="text-xs text-gray-500">{o.detalhe}</p>
              </button>
            ))}
          </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select className="input min-w-[16rem]" value={escolha} onChange={(e) => setEscolha(e.target.value)}>
            <option value="">Outras configurações…</option>
            {s?.templateCode && !sugestaoJaListada && (
              <option value={`tpl:${s.templateCode}`}>{s.name} (sugerida)</option>
            )}
            {(cfgs.data?.items ?? []).filter((c) => c.active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            {(cfgs.data?.templates ?? []).map((t) => <option key={t.code} value={`tpl:${t.code}`}>{t.name} (modelo padrão)</option>)}
          </select>
          <button className="btn-primary" disabled={!escolha || salvar.isPending} onClick={() => { setErro(null); salvar.mutate(); }}>
            {salvar.isPending ? "Salvando…" : "Usar esta configuração"}
          </button>
          {erro && <p className="w-full text-sm text-danger-600">{erro}</p>}
        </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">Peça a um gestor para definir a configuração.</p>
      )}
    </div>
  );
}
